import { isNumberString } from "@corca/core";
import { sortBy } from "es-toolkit";

import {
  createKnownSuggestionMap,
  createPowerOperatorItem,
  createSingleLetterSuggestionMap,
  getFlattenVariants,
  getSplitStringSuggestions,
  increasePriority,
  mediumPriority,
  type TextVariantProps,
  type SuggestionVariant,
  filterVariants,
} from "./multiSuggestionsProvider/utils";
import { symbolJson } from "./operatorSymbolSuggestionsProvider";

import type {
  FunctionSuggestionProps,
  SingleSuggestion,
  SuggestionProvider,
} from "@/components/suggestions/types";
import type { NodeModel } from "@/models/nodes/types";

const FINAL_VARIANTS_LIMIT = 5;

const knownSuggestionMap = createKnownSuggestionMap();
const knownSymbolKeywords = Array.from(knownSuggestionMap.keys());

const singleLetterSuggestionMap = createSingleLetterSuggestionMap(
  new Map([
    ["e", knownSuggestionMap.get("e") ?? []],
    ["i", knownSuggestionMap.get("i") ?? []],
    ["x", knownSuggestionMap.get("x") ?? []],
    ["y", knownSuggestionMap.get("y") ?? []],
    ["z", knownSuggestionMap.get("z") ?? []],
  ]),
);
const singleLetterSymbols = Array.from(singleLetterSuggestionMap.keys());

export const multiSuggestionsProvider: SuggestionProvider = {
  type: "multi",
  search: (str, doc) => {
    if (str.length <= 1) return [];

    if (!/\w+/.test(str)) return [];

    if (str.includes("-")) {
      return [collectNegativeArgumentVariants(str, doc)];
    }

    const tokens = tokenizeText(str);

    if (tokens.length > 10) {
      return [];
    }

    const props = createTextVariantProps(doc);

    let variants = collectVariants(tokens, props);

    let flattenVariants = filterVariants(getFlattenVariants(variants));

    const greedy = flattenVariants.length === 0 && str.length < 3;

    // if no variants found and input is short, perform greedy symbol matching
    if (greedy) {
      props.knownSymbolKeywords = singleLetterSymbols;
      props.knownSuggestionMap = singleLetterSuggestionMap;

      variants = collectVariants(tokens, props);
      flattenVariants = filterVariants(getFlattenVariants(variants));
    }

    for (const variant of flattenVariants) {
      variant.priority = increasePriority(variant.priority, variant.maxRepetition ?? 1, 1.5);
      variant.priority -= 0.5;

      if (greedy) {
        variant.priority -= 1;
        variant.popupGroup = "multi";
      }
    }

    flattenVariants = sortBy(flattenVariants, [(v) => -v.priority]);

    return [selectFunctionBucketVariants(flattenVariants, FINAL_VARIANTS_LIMIT)];
  },
};

/**
 * group variants by their function sequence so all structural readings of the
 * best-matching function (sin(2x²), sin²(x²), ...) surface before sibling
 * functions (arcsin, sinh) fill the remaining slots
 */
function selectFunctionBucketVariants(variants: SuggestionVariant[], limit: number) {
  const buckets = new Map<string, SuggestionVariant[]>();

  for (const variant of variants) {
    const key = variant.items
      .filter((item) => item.type === "function")
      .map((item) => item.symbol)
      .join("×");
    let bucket = buckets.get(key);
    if (!bucket) buckets.set(key, (bucket = []));
    bucket.push(variant);
  }

  return Array.from(buckets.values()).flat().slice(0, limit);
}

const minusSuggestion = symbolJson.find((s) => s.item.key === "symbol-subtraction");

type SearchDoc = Parameters<SuggestionProvider["search"]>[1];

function tokenizeText(text: string) {
  const tokens = text.split(/(\d+)/).filter(Boolean);
  if (isNumberString(tokens[0])) {
    tokens.unshift("");
  }
  return tokens;
}

function createTextVariantProps(doc: SearchDoc): TextVariantProps {
  const props: TextVariantProps = {
    knownSymbolKeywords,
    knownSuggestionMap,
    nodeLookup: doc.nodeLookup,
    existingNodes: new Map(),
  };

  const nodeKeyMap = doc.nodeLookup?.groupByKey({ uniqBy: "labels" });

  if (nodeKeyMap) {
    for (const { base, type, ids } of nodeKeyMap.values()) {
      if (type === "function") continue;
      props.existingNodes.set(base, []);
      const firstId = ids.values().next().value;
      const node = firstId ? (doc.nodes.get(firstId) as NodeModel<"variable" | "constant">) : null;
      props.existingNodes.get(base)?.push(node);
    }
  }

  return props;
}

/**
 * only support a single minus right after a leading function name (sin-x, sin-1, sin-2x);
 * chains like cos-xsin-y are ambiguous, minus between plain symbols is subtraction,
 * and function arguments (sin-cosx) break the sibling-function heuristic in
 * buildSingleNodeFromMultiSuggest, which closes the outer argument on the next function
 */
function collectNegativeArgumentVariants(str: string, doc: SearchDoc): SuggestionVariant[] {
  const match = /^([a-z]+)-([^-]+)$/.exec(str);
  if (!match || !minusSuggestion) return [];

  const [, head, rest] = match;

  const functionSuggestions = (knownSuggestionMap.get(head) ?? []).filter(
    (s) => s.item.type === "function",
  );
  if (functionSuggestions.length === 0) return [];

  const props = createTextVariantProps(doc);
  const restTokens = tokenizeText(rest);

  const restVariants = collectNonFunctionVariants(restTokens, props);

  const variants: SuggestionVariant[] = [];

  for (const functionSuggestion of functionSuggestions) {
    for (const restVariant of restVariants) {
      variants.push({
        variantTypes: ["plain", "plain", ...restVariant.variantTypes],
        type: "multi",
        items: [functionSuggestion.item, minusSuggestion.item, ...restVariant.items],
        priority: mediumPriority([functionSuggestion.priority, restVariant.priority]),
        popupGroup: "multi",
      });
    }
  }

  variants.push(...collectNegativePowerVariants(functionSuggestions, restTokens, props));

  return selectFunctionBucketVariants(sortBy(variants, [(v) => -v.priority]), FINAL_VARIANTS_LIMIT);
}

function collectNonFunctionVariants(tokens: string[], props: TextVariantProps) {
  return getFlattenVariants(collectVariants(tokens, props)).filter(
    (variant) => !variant.items.some((item) => item.type === "function"),
  );
}

/**
 * mirror the positive function-power reading for a negative exponent:
 * sin-1 → sin^-1(), sin-2x2 → sin^-2(x²); exponent -1 is allowed since,
 * unlike ^1, it isn't a no-op
 */
function collectNegativePowerVariants(
  functionSuggestions: SingleSuggestion[],
  restTokens: string[],
  props: TextVariantProps,
): SuggestionVariant[] {
  if (restTokens[0] !== "") return [];

  const num = restTokens[1];
  const argTokens = restTokens.slice(2);

  const argVariants: SuggestionVariant[] = argTokens.length
    ? collectNonFunctionVariants(argTokens, props)
    : [{ variantTypes: [], type: "multi", items: [], priority: 1 }];

  const variants: SuggestionVariant[] = [];

  for (const functionSuggestion of functionSuggestions) {
    const funcProps = functionSuggestion.item.props as FunctionSuggestionProps | undefined;
    if ((funcProps?.powerPlacement ?? "child") !== "child") continue;

    for (const argVariant of argVariants) {
      variants.push({
        variantTypes: ["power", ...argVariant.variantTypes],
        type: "multi",
        items: [functionSuggestion.item, createPowerOperatorItem(`-${num}`), ...argVariant.items],
        priority: mediumPriority([functionSuggestion.priority, argVariant.priority]),
        popupGroup: "multi",
      });
    }
  }

  return variants;
}

function collectVariants(tokens: string[], props: TextVariantProps): SuggestionVariant[][] {
  const variants: SuggestionVariant[][] = [];

  for (let i = 0; i < tokens.length; i += 2) {
    const text = tokens[i];
    const num = tokens[i + 1];
    const isLast = i >= tokens.length - 2;
    variants.push(getSplitStringSuggestions(i, text, num, isLast, props));
  }

  return variants;
}
